import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { CmpTypographyField } from '../../../component/ToolBox/ToolBox';
import { exportToExcel, fAlertToast } from '../../../Utility/Utilitys';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { DataGridPro, GridCellEditStopReasons, GridEditInputCell } from '@mui/x-data-grid-pro';

const toolbar = {
  backgroundColor: '#0d73bb',
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  minHeight: '40px !important'
};

export const ItemVariantModal = ({
  data,
  open,
  onClose,
  subDataGrid,
  setSubDataGrid,
  calculate_Qty_Variant
}) => {
  const vMediaQuery = useMediaQuery('max-width:900px');

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const updateValues = (newRow, oldRow = {}) => {
    // base fields
    const piecesRaw = newRow.GM_Pieces ?? oldRow.GM_Pieces ?? '';
    const piecesNum = piecesRaw === '' ? 0 : Math.max(0, toNum(piecesRaw));

    const ctnRaw = newRow.GMS_CTN ?? oldRow.GMS_CTN ?? '';
    const ctnNum = ctnRaw === '' ? 0 : Math.max(0, toNum(ctnRaw));

    const wgtRaw = newRow.GMS_Weight ?? oldRow.GMS_Weight ?? '';
    const wgtNum = wgtRaw === '' ? 0 : Math.max(0, toNum(wgtRaw));

    // price fields: keep raw for the field being edited
    const costPerKgRawIn = newRow.GMS_CostPerKg ?? oldRow.GMS_CostPerKg ?? '';
    const costPerKgNumIn = costPerKgRawIn === '' ? 0 : Math.max(0, toNum(costPerKgRawIn));

    const priceRawIn = newRow.GMS_Cost ?? oldRow.GMS_Cost ?? '';
    const priceNumIn = priceRawIn === '' ? 0 : Math.max(0, toNum(priceRawIn));

    const changedCostPerKg =
      newRow.GMS_CostPerKg !== undefined && newRow.GMS_CostPerKg !== oldRow.GMS_CostPerKg;
    const changedPrice = newRow.GMS_Cost !== undefined && newRow.GMS_Cost !== oldRow.GMS_Cost;

    const denom = wgtNum * piecesNum;

    let nextCostPerKgNum = costPerKgNumIn;
    let nextPriceNum = priceNumIn;

    if (changedCostPerKg) {
      nextPriceNum = denom > 0 ? costPerKgNumIn * denom : 0; // compute the OTHER field only
    }
    if (changedPrice) {
      nextCostPerKgNum = denom > 0 ? priceNumIn / denom : 0; // compute the OTHER field only
    }

    // Keep the edited field as-typed; update the other one numerically.
    const out = {
      ...oldRow,
      ...newRow,
      GM_Pieces: piecesRaw,
      GMS_CTN: ctnRaw,
      GMS_Weight: wgtRaw,
      GMS_CostPerKg: changedPrice ? nextCostPerKgNum.toFixed(2) : costPerKgRawIn, // only format when computed
      GMS_Cost: changedCostPerKg ? nextPriceNum.toFixed(2) : priceRawIn, // only format when computed
      GMS_NetWeight: ctnNum * piecesNum * wgtNum,
      GMS_NetCost: nextPriceNum * ctnNum,
      NetLPCost: piecesNum * toNum(newRow.LPCost ?? oldRow.LPCost ?? 0)
    };

    setSubDataGrid((prev) => prev.map((r) => (r.id === out.id ? out : r)));
    return out;
  };

  const setGMS_CTN = (params) => {
    const { row, value } = params;
    const nextRow = updateValues({ ...row, GMS_CTN: value }, row);
    const updatedRows = subDataGrid.map((itm) => (itm.id === nextRow.id ? nextRow : itm));
    setSubDataGrid(updatedRows);
    return nextRow;
  };
  const setGM_Pieces = (params) => {
    const { row, value } = params;
    const nextRow = updateValues({ ...row, GM_Pieces: value }, row);
    const updatedRows = subDataGrid.map((itm) => (itm.id === nextRow.id ? nextRow : itm));
    setSubDataGrid(updatedRows);
    return nextRow;
  };
  const setGMS_CostPerKg = (params) => {
    const { row, value } = params;
    const nextRow = updateValues({ ...row, GMS_CostPerKg: value }, row);
    const updatedRows = subDataGrid.map((itm) => (itm.id === nextRow.id ? nextRow : itm));
    setSubDataGrid(updatedRows);
    return nextRow;
  };
  const setGMS_Cost = (params) => {
    const { row, value } = params;
    const nextRow = updateValues({ ...row, GMS_Cost: value }, row);
    const updatedRows = subDataGrid.map((itm) => (itm.id === nextRow.id ? nextRow : itm));
    setSubDataGrid(updatedRows);
    return nextRow;
  };

  const setLPCost = (params) => {
    const { row, value } = params;
    const nextRow = updateValues({ ...row, LPCost: value }, row);
    const updatedRows = subDataGrid.map((itm) => (itm.id === nextRow.id ? nextRow : itm));
    setSubDataGrid(updatedRows);
    return nextRow;
  };

  // ---- NAV CONSTANTS (left-to-right order) ----
  const FIELD_ORDER = ['GMS_CTN', 'GM_Pieces', 'GMS_CostPerKg', 'GMS_Cost', 'LPCost'];

  const FIELD_PREFIX = {
    GMS_CTN: 'ctn',
    GM_Pieces: 'pieces',
    GMS_CostPerKg: 'costkg',
    GMS_Cost: 'price',
    LPCost: 'avgcost'
  };

  const idFor = (field, rowIdx) => `${FIELD_PREFIX[field]}-${rowIdx}`;

  const isEnterOnly = (e) => e.key === 'Enter' && !e.shiftKey;
  const isShiftEnter = (e) => e.key === 'Enter' && e.shiftKey;

  // Focus next/prev field horizontally; wrap to next/prev row as needed
  const focusHoriz = (params, dir = +1) => {
    const api = params.api;
    const curRow = api.getRowIndexRelativeToVisibleRows(params.id);
    const curFieldIdx = FIELD_ORDER.indexOf(params.field);
    if (curFieldIdx === -1) return;

    let nextFieldIdx = curFieldIdx + dir;
    let nextRow = curRow;

    if (nextFieldIdx >= FIELD_ORDER.length) {
      nextFieldIdx = 0;
      nextRow = curRow + 1;
    }
    if (nextFieldIdx < 0) {
      nextFieldIdx = FIELD_ORDER.length - 1;
      nextRow = curRow - 1;
    }
    if (nextRow < 0) return; // stop at top (or wrap to last if you prefer)

    api.scrollToIndexes({ rowIndex: nextRow });
    const targetId = idFor(FIELD_ORDER[nextFieldIdx], nextRow);
    requestAnimationFrame(() => document.getElementById(targetId)?.focus());
  };

  //focus the edit field one by one
  const fieldToIdPrefix = (field) => (field === 'GM_Pieces' ? 'pieces' : 'cost');

  const focusCellByIndex = (api, rowIndex, field) => {
    if (rowIndex < 0) return;
    // Bring target row into view first
    api.scrollToIndexes({ rowIndex });

    // Focus the specific input by ID after the row renders
    const targetId = idFor(field, rowIndex);
    // allow the grid to render the row, sometimes two frames helps with virtualization
    requestAnimationFrame(() => {
      const el1 = document.getElementById(targetId);
      if (el1) {
        el1.focus();
        return;
      }
      requestAnimationFrame(() => {
        document.getElementById(targetId)?.focus();
      });
    });
  };

  // focus to edit field each row in horizontally  when 0 it changed '' Optional: reverse on Shift+Enter

  const isZeroish = (v) => {
    const s = String(v ?? '').trim();
    if (s === '') return false;
    const n = Number(s);
    return !Number.isNaN(n) && n === 0;
  };

  const fContent_Focus = (params, e, setSubDataGrid) => {
    // params.id -> row id, params.field -> column field
    const { id, field } = params;
    const raw = e.target.value;

    if (!isZeroish(raw)) return;

    setSubDataGrid((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: '' } : r)));
  };

  // blur to edit field each row in horizontally  when ' ' it changed 0
  const toFixedNum = (n, d = 2) => Number(Number(n).toFixed(d));
  const toNumOr0 = (s) => {
    const t = String(s ?? '').trim();
    if (t === '') return 0;
    const n = Number(t);
    return Number.isFinite(n) ? n : 0;
  };
  // params-based blur (recommended)
  const fContent_Blur = (params, e, setSubDataGrid, { decimals = 2, min = 0 } = {}) => {
    const { id, field, row } = params;
    const raw = e.target.value;
    const num = toNumOr0(raw);
    const clamped = Math.max(min, num);
    const committed = decimals == null ? clamped : toFixedNum(clamped, decimals);

    setSubDataGrid((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, [field]: committed };
        // If you have updateValues(newRow, oldRow), keep derived fields in sync:
        return typeof updateValues === 'function' ? updateValues(next, r) : next;
      })
    );
  };

  // Utility: normalize and validate a single row
  const validateRow = (row) => {
    const pieces = Number(row.GM_Pieces ?? row.GM_Pieces ?? 0);
    const ctn = Number(row.GMS_CTN ?? 0);
    const cost = Number(row.GMS_Cost ?? 0);

    const errors = [];

    // Rule 1: CTN must be > 0 when Pieces != 0
    if (pieces !== 0 && ctn <= 0) {
      errors.push('CTN must be > 0 when Pieces ≠ 0');
    }

    // Rule 2: Pieces must be > 0 when CTN != 0
    if (ctn !== 0 && pieces <= 0) {
      errors.push('Pieces must be > 0 when CTN ≠ 0');
    }

    // Rule 3: Cost must be != 0 when both CTN & Pieces > 0
    if (ctn > 0 && pieces > 0 && cost === 0) {
      errors.push('Cost must be ≠ 0 when CTN > 0 and Pieces > 0');
    }
    if (cost > 0 && pieces === 0 && ctn === 0) {
      errors.push('CTN & Pieces must be ≠ 0 when Cost > 0 ');
    }

    return { valid: errors.length === 0, errors, pieces, ctn, cost };
  };

  // Validate all rows and group errors by row id
  const validateRowsById = (rows) => {
    const errorsById = {};
    for (const r of rows) {
      const { valid, errors } = validateRow(r);
      if (!valid) errorsById[r.id] = errors;
    }
    return {
      valid: Object.keys(errorsById).length === 0,
      errorsById
    };
  };

  const fApply = () => {
    // validate every row (or filter to updated ones if you prefer)
    const { valid, errorsById } = validateRowsById(subDataGrid);

    if (!valid) {
      const [firstId, msgs] = Object.entries(errorsById)[0];
      fAlertToast('FAILED', `Row ID ${firstId}: ${msgs[0]}`);
      return;
    }
    let packQty = 0;
    if (totals?.GMS_CTN && totals.GMS_CTN !== 0) {
      packQty = totals.GMS_NetWeight / totals.GMS_CTN;
    }

    let unitRate = 0;
    if (totals?.GMS_CTN && totals.GMS_CTN !== 0) {
      unitRate = totals.GMS_NetCost / totals.GMS_CTN;
    }

    console.log('data:', data, packQty.toFixed(3), totals.GMS_CTN.toFixed(3), unitRate.toFixed(4));

    calculate_Qty_Variant(data, packQty.toFixed(3), totals.GMS_CTN.toFixed(3), unitRate.toFixed(4));

    onClose();
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 140,
      hide: true
    },
    {
      field: 'GMS_AltItmCd',
      headerName: 'ItemCode',
      width: 140,
      hide: true
    },
    {
      field: 'GMS_Name1',
      headerName: 'ItemName',
      width: 170
    },
    {
      field: 'GMS_CTN',
      headerName: 'CTN',
      width: vMediaQuery ? 50 : 90,
      type: 'number',
      cellClassName: 'editable-cell',

      renderCell: (params) => {
        const { row } = params;
        if (row.isSummaryRow) return <span>{params.value}</span>;
        const rowIdx = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return (
          <TextField
            id={idFor(params.field, rowIdx)}
            name={params.field}
            value={row?.GMS_CTN ?? ''} // show whatever is in state ('' allowed)
            onKeyDown={(e) => {
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
                e.stopPropagation();
              if (e.key === '-' || e.key.toLowerCase() === 'e') e.preventDefault();
              if (isEnterOnly(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusHoriz(params, +1);
              }
              if (isShiftEnter(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusHoriz(params, -1);
              }
            }}
            onChange={(e) =>
              setGMS_CTN({ ...params, value: e.target.value === '' ? '' : e.target.value })
            }
            onFocus={(e) => fContent_Focus(params, e, setSubDataGrid)}
            onBlur={(e) => fContent_Blur(params, e, setSubDataGrid, { decimals: 2, min: 0 })}
            variant="outlined"
            size="small"
            inputProps={{
              inputMode: 'decimal',
              type: 'text', // <-- important: allow empty/backspace
              pattern: '[0-9]*[.]?[0-9]*',
              style: { textAlign: 'center' }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'right',
                padding: '4px 12px',
                fontSize: '13px',
                backgroundColor: '#edfaff',
                // border: '1px solid lightgray',
                borderRadius: '6px !important'
              }
            }}
          />
        );
      }
    },

    {
      field: 'GM_Pieces',
      headerName: 'Pieces',
      width: vMediaQuery ? 70 : 90,
      type: 'number',

      cellClassName: 'editable-cell',
      // renderCell: (params) =>
      //   params.row.isSummaryRow ? <span>{params.value}</span> : <EditCell {...params} />
      renderCell: (params) => {
        const { row } = params;
        if (row.isSummaryRow) return <span>{params.value}</span>;
        const rowIdx = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return (
          <TextField
            id={idFor(params.field, rowIdx)}
            name={params.field}
            value={row?.GM_Pieces ?? ''} // show whatever is in state ('' allowed)
            onKeyDown={(e) => {
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
                e.stopPropagation();
              if (e.key === '-' || e.key.toLowerCase() === 'e') e.preventDefault();
              if (isEnterOnly(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusHoriz(params, +1);
              } // -> Cost/Kg
              if (isShiftEnter(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusHoriz(params, -1);
              } // <- CTN
            }}
            onChange={(e) =>
              setGM_Pieces({ ...params, value: e.target.value === '' ? '' : e.target.value })
            }
            onFocus={(e) => fContent_Focus(params, e, setSubDataGrid)}
            onBlur={(e) => fContent_Blur(params, e, setSubDataGrid, { decimals: 2, min: 0 })}
            variant="outlined"
            size="small"
            inputProps={{
              inputMode: 'decimal',
              type: 'text', // <-- important: allow empty/backspace
              pattern: '[0-9]*[.]?[0-9]*',
              style: { textAlign: 'center' }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'right',
                padding: '4px 12px',
                fontSize: '13px',
                backgroundColor: '#edfaff',
                // border: '1px solid lightgray',
                borderRadius: '6px !important'
              }
            }}
          />
        );
      }
    },
    { field: 'GMS_Weight', headerName: 'Weight', width: vMediaQuery ? 70 : 100, type: 'number' },
    {
      field: 'GMS_NetWeight',
      headerName: 'Net Weight',
      width: vMediaQuery ? 70 : 120,
      type: 'number',
      editable: false,
      cellClassName: 'readonly-cell'
    },
    {
      field: 'GMS_CostPerKg',
      headerName: 'Cost/Kg',
      width: vMediaQuery ? 70 : 100,
      type: 'number',
      cellClassName: 'editable-cell',

      renderCell: (params) => {
        const { row } = params;
        if (row.isSummaryRow) return <span>{params.value}</span>;
        const rowIdx = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return (
          <TextField
            id={idFor(params.field, rowIdx)}
            name={params.field}
            value={row?.GMS_CostPerKg ?? ''} // show whatever is in state ('' allowed)
            onKeyDown={(e) => {
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
                e.stopPropagation();
              if (e.key === '-' || e.key.toLowerCase() === 'e') e.preventDefault();
              if (isEnterOnly(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusHoriz(params, +1);
              } // -> Unit Price
              if (isShiftEnter(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusHoriz(params, -1);
              } // <- Pieces
            }}
            onChange={(e) =>
              setGMS_CostPerKg({ ...params, value: e.target.value === '' ? '' : e.target.value })
            }
            onFocus={(e) => fContent_Focus(params, e, setSubDataGrid)}
            onBlur={(e) => fContent_Blur(params, e, setSubDataGrid, { decimals: 2, min: 0 })}
            variant="outlined"
            size="small"
            inputProps={{
              inputMode: 'decimal',
              type: 'text', // <-- important: allow empty/backspace
              pattern: '[0-9]*[.]?[0-9]*',
              style: { textAlign: 'center' }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'right',
                padding: '4px 12px',
                fontSize: '13px',
                backgroundColor: '#edfaff',
                // border: '1px solid lightgray',
                borderRadius: '6px !important'
              }
            }}
          />
        );
      }
    },
    {
      field: 'GMS_Cost',
      headerName: 'Unit Price',
      width: vMediaQuery ? 70 : 100,
      type: 'number',
      cellClassName: 'editable-cell',

      renderCell: (params) => {
        const { row } = params;
        if (row.isSummaryRow) return <span>{params.value}</span>;
        const rowIdx = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return (
          <TextField
            id={idFor(params.field, rowIdx)}
            name={params.field}
            value={row?.GMS_Cost ?? ''} // show whatever is in state ('' allowed)
            onKeyDown={(e) => {
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
                e.stopPropagation();
              if (e.key === '-' || e.key.toLowerCase() === 'e') e.preventDefault();
              if (isEnterOnly(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusHoriz(params, +1);
              } // -> Avg.Cost/KG
              if (isShiftEnter(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusHoriz(params, -1);
              } // <- Cost/Kg
            }}
            onChange={(e) =>
              setGMS_Cost({ ...params, value: e.target.value === '' ? '' : e.target.value })
            }
            onFocus={(e) => fContent_Focus(params, e, setSubDataGrid)}
            onBlur={(e) => fContent_Blur(params, e, setSubDataGrid, { decimals: 2, min: 0 })}
            variant="outlined"
            size="small"
            inputProps={{
              inputMode: 'decimal',
              type: 'text', // <-- important: allow empty/backspace
              pattern: '[0-9]*[.]?[0-9]*',
              style: { textAlign: 'center' }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'right',
                padding: '4px 12px',
                fontSize: '13px',
                backgroundColor: '#edfaff',
                // border: '1px solid lightgray',
                borderRadius: '6px !important'
              }
            }}
          />
        );
      }
    },
    {
      field: 'GMS_NetCost',
      headerName: 'Net Amount',
      width: vMediaQuery ? 70 : 120,
      type: 'number',
      cellClassName: 'readonly-cell'
    },
    {
      field: 'LPCost',
      headerName: 'Avg.Cost/KG',
      width: vMediaQuery ? 70 : 100,
      type: 'number',
      cellClassName: 'editable-cell',
      renderCell: (params) => {
        const { row } = params;
        if (row.isSummaryRow) return <span>{params.value}</span>;
        const rowIdx = params.api.getRowIndexRelativeToVisibleRows(params.id);

        return (
          <TextField
            id={idFor(params.field, rowIdx)}
            name={params.field}
            value={row?.LPCost ?? ''} // show whatever is in state ('' allowed)
            onKeyDown={(e) => {
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
                e.stopPropagation();
              if (e.key === '-' || e.key.toLowerCase() === 'e') e.preventDefault();
              if (isEnterOnly(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusHoriz(params, +1);
              } // wraps to next row CTN
              if (isShiftEnter(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusHoriz(params, -1);
              } // <- Unit Price
            }}
            onChange={(e) =>
              setLPCost({ ...params, value: e.target.value === '' ? '' : e.target.value })
            }
            onFocus={(e) => fContent_Focus(params, e, setSubDataGrid)}
            onBlur={(e) => fContent_Blur(params, e, setSubDataGrid, { decimals: 2, min: 0 })}
            variant="outlined"
            size="small"
            inputProps={{
              inputMode: 'decimal',
              type: 'text', // <-- important: allow empty/backspace
              pattern: '[0-9]*[.]?[0-9]*',
              style: { textAlign: 'center' }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'right',
                padding: '4px 12px',
                fontSize: '13px',
                backgroundColor: '#edfaff',
                // border: '1px solid lightgray',
                borderRadius: '6px !important'
              }
            }}
          />
        );
      }
    },
    {
      field: 'NetLPCost',
      headerName: 'Avg.Cost/CTN',
      width: vMediaQuery ? 70 : 120,
      type: 'number',
      cellClassName: 'readonly-cell'
    }
  ];

  const totals = React.useMemo(() => {
    return subDataGrid?.reduce(
      (a, r) => ({
        GMS_CTN: a.GMS_CTN + Number(r.GMS_CTN || 0),
        GMS_NetWeight: a.GMS_NetWeight + Number(r.GMS_NetWeight || 0),
        GMS_NetCost: a.GMS_NetCost + Number(r.GMS_NetCost || 0)
      }),
      { GMS_CTN: 0, GMS_NetWeight: 0, GMS_NetCost: 0 }
    );
  }, [subDataGrid]);

  const handleExport = () => {
    exportToExcel(subDataGrid, 'PurchaseQuantity_data', 'PurchaseQuantity');
  };

  const balQty = data.OrdQty - data.ExistingPur - totals.GMS_NetWeight;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullScreen={vMediaQuery}>
      <Toolbar sx={toolbar}>
        <DialogTitle
          sx={{ backgroundColor: '#0d73bb', color: 'white', padding: '0 4px !important' }}>
          Quantity Variant
        </DialogTitle>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Toolbar>

      <DialogContent sx={{ '&.MuiDialogContent-root': { padding: '10px !important' } }}>
        <Grid container columnSpacing={2} rowSpacing={0} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={5} md={5} lg={5}>
            <Grid
              container
              spacing={1}
              columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              sx={{ alignItems: 'center' }}>
              <Grid item xs={7} sm={2} lg={1.5} md={1.5}>
                <CmpTypographyField xcomponent={'span'} xText={'Item'} />
              </Grid>
              <Grid item xs={1} sm={0.5} lg={0.5} md={0.5}>
                <CmpTypographyField xcomponent={'span'} xText={':'} />
              </Grid>
              <Grid item xs={2} sm={9.5} lg={10} md={10}>
                <CmpTypographyField
                  xcomponent={'span'}
                  xText={`${data?.ALTItmCd} -${data?.ALTItmName}`}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={2} md={2} lg={2}>
            <Grid
              container
              spacing={1}
              columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              sx={{ alignItems: 'center' }}>
              <Grid item xs={7} sm={5} lg={4} md={4}>
                <CmpTypographyField xcomponent={'span'} xText={'Pack'} />
              </Grid>
              <Grid item xs={1} sm={1.5} lg={1} md={1}>
                <CmpTypographyField xcomponent={'span'} xText={':'} />
              </Grid>
              <Grid item xs={2} sm={5.5} lg={7} md={7}>
                <CmpTypographyField xcomponent={'span'} xText={data.Ad_PCQty} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={2} md={2} lg={2}>
            <Grid
              container
              spacing={1}
              columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              sx={{ alignItems: 'center' }}>
              <Grid item xs={7} sm={6} lg={6} md={6}>
                <CmpTypographyField xcomponent={'span'} xText={'Ext.Pur'} />
              </Grid>
              <Grid item xs={1} sm={1} lg={1} md={1}>
                <CmpTypographyField xcomponent={'span'} xText={':'} />
              </Grid>
              <Grid item xs={2} sm={5} lg={5} md={5}>
                <CmpTypographyField xcomponent={'span'} xText={data.ExistingPur} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={3} md={3} lg={3}>
            <Grid
              container
              spacing={1}
              columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              sx={{ alignItems: 'center' }}>
              <Grid item xs={7} sm={4} lg={4} md={4}>
                <CmpTypographyField xcomponent={'span'} xText={'Req.CTN'} />
              </Grid>
              <Grid item xs={1} sm={1} lg={1} md={1}>
                <CmpTypographyField xcomponent={'span'} xText={':'} />
              </Grid>
              <Grid item xs={2} sm={4} lg={4} md={4}>
                <CmpTypographyField xcomponent={'span'} xText={data.ExistingPurCTN.toFixed(2)} />
              </Grid>
              <Grid
                item
                xs={2}
                sm={3}
                lg={3}
                md={3}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}>
                <Tooltip title="Export" placement="top">
                  <IconButton
                    color="primary"
                    onClick={handleExport}
                    size="small"
                    sx={{
                      padding: '0px 2px',
                      '& .MuiSvgIcon-root': {
                        padding: '0px',
                        fontSize: '20px'
                      }
                    }}>
                    <DownloadOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12} mt={1}>
            <Box
              sx={{
                height: '55vh',
                width: '100%'
              }}>
              <DataGridPro
                className="gridTable"
                rowHeight={30}
                headerHeight={30}
                rows={subDataGrid}
                columns={columns}
                getRowId={(row) => row.id}
                pinnedRows={{
                  bottom: [
                    {
                      id: 'footer',
                      GMS_Name1: 'TOTAL',
                      GMS_CTN: totals.GMS_CTN.toFixed(2),
                      GMS_NetWeight: totals.GMS_NetWeight.toFixed(4),
                      GMS_NetCost: totals.GMS_NetCost.toFixed(3),
                      isSummaryRow: true
                    }
                  ]
                }}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      id: false,
                      GMS_AltItmCd: false
                    }
                  }
                }}
                disableRowSelectionOnClick
                isCellEditable={(params) => !params.row?.isSummaryRow}
                getRowClassName={(params) => (params.row?.isSummaryRow ? 'summary-row' : '')}
                isRowSelectable={(params) => !params.row?.isSummaryRow}
                hideFooter
                disableSelectionOnClick
              />
            </Box>

            <Grid container>
              <Grid item xs={6} textAlign={'right'}>
                <Button onClick={fApply} variant="contained" size="small" sx={{ m: 1 }}>
                  Apply
                </Button>
              </Grid>
              <Grid item xs={6} textAlign={'right'}>
                <Grid container>
                  <Grid item xs={9.5}>
                    OrderQty
                  </Grid>
                  <Grid item xs={0.5}>
                    :
                  </Grid>
                  <Grid item xs={2}>
                    {' '}
                    {data.OrdQty}
                  </Grid>
                  <Grid item xs={9.5}>
                    Existing Purchase
                  </Grid>
                  <Grid item xs={0.5}>
                    :
                  </Grid>
                  <Grid item xs={2}>
                    {' '}
                    {data.ExistingPur}
                  </Grid>
                  <Grid item xs={9.5}>
                    Current Purchase
                  </Grid>
                  <Grid item xs={0.5}>
                    :
                  </Grid>
                  <Grid item xs={2}>
                    {' '}
                    {totals.GMS_NetWeight.toFixed(2)}
                  </Grid>
                  <Grid item xs={9.5}>
                    Balance Qty
                  </Grid>
                  <Grid item xs={0.5}>
                    :
                  </Grid>
                  <Grid item xs={2}>
                    {' '}
                    {balQty > 0 ? balQty.toFixed(2) : 0}
                  </Grid>
                </Grid>
                {/* <Box>OrderQty : {data.OrdQty}</Box>
                <Box>Existing Purchase : {data.ExistingPur}</Box>
                <Box>Current Purchase : {totals.GMS_NetWeight}</Box>
                <Box>Balance Qty : {balQty > 0 ? balQty : 0}</Box> */}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
